import { computed, inject, Type } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { GridType } from '@data-access/grid-views';
import {
  constructGridFilters,
  DEFAULT_CHECKBOX_PROPS,
  LoadGridPayload,
  SITE_URL,
  STUDY_URL,
  withGridConfigFeatures,
  WORKSPACE_URLS,
} from '@data-access/shared';
import {
  submissionListInitialState,
  SUBMISSION_LIST_GRID_STATE,
} from '@data-access/submissions/constants/submission-list.constant';
import { SubmissionsListService } from '@data-access/submissions/services';
import {
  AssignmentPayload,
  BatchAssignmentsPayload,
  SMEQCListParams,
  SMEQCUser,
  SubmissionGridTypes,
  SubmissionListItem,
  SubmissionListResponse,
  SubmissionListSignalState,
} from '@data-access/submissions/types';
import {
  patchState,
  signalStore,
  withComputed,
  withFeature,
  withMethods,
  withProps,
  withState,
} from '@ngrx/signals';
import { WcgTableUtilsService } from '@shared/services/wcg-table-utils.service';
import {
  BuildLinkFromValue,
  GridColumnCasePipe,
} from '@wcg/frontend-framework';
import { withSubmissionEntities } from './entities/with-submission.entities';
import { withSMEQCEntities } from './entities/with-sme-qc.entities';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { filter, map, Observable, pipe, switchMap, tap } from 'rxjs';
import { tapResponse } from '@ngrx/operators';
import { ShallowCamelCasePipe } from '@shared/pipes/shallow-camel-case.pipe';
import { withGridCheckBoxFeature } from '@data-access/shared/store/features/with-grid-checkbox-feature.store';
import { UserService } from '@user/user.service';
import { User } from '@iris-sites/interfaces/sites.interfaces';

export const SUBMISSIONS_LIST_SIGNAL_STORE = signalStore(
  withState(() => inject(SUBMISSION_LIST_GRID_STATE)),
  withProps((store) => {
    const _userService = inject(UserService);
    const $isInternalSignal = toSignal(
      _userService.user$.pipe(
        filter((user: User | undefined): user is User => !!user),
        map((user) => !!user.is_internal),
      ),
      { initialValue: false },
    );

    return {
      _buildLinkFromValue: new BuildLinkFromValue(),
      _gridCasePipe: new GridColumnCasePipe(),
      _shallowCamelCasePipe: new ShallowCamelCasePipe(),
      _wcgTableUtilService: inject(WcgTableUtilsService),
      _submissionsListService: inject(SubmissionsListService),
      _userService,
      $isInternalSignal,
      store,
    };
  }),
  withFeature(() => withGridConfigFeatures()),
  withFeature(() => withSubmissionEntities()),
  withFeature(() => withSMEQCEntities()),
  withFeature(() => withGridCheckBoxFeature()),
  withComputed(
    ({
      error,
      _submissionEntities,
      _smeEntities,
      _qcEntities,
      selectedView,
      total,
      isLoading,
      currentUrl,
      submissionListScope,
      isUpdatingSMEQC,
      gridType,
      $isInternalSignal,
      isAllSelected,
      isIndeterminate,
      selectedRows,
      columnsCopy,
    }) => ({
      $currentUrl: computed(() => currentUrl()),
      $submissionListScope: computed(() => submissionListScope()),
      $error: computed(() => error()),

      $gridType: computed(() => {
        const _gridType: GridType = gridType();
        return _gridType;
      }),
      $isLoading: computed(() => isLoading()),
      $isUpdatingSMEQC: computed(() => isUpdatingSMEQC()),
      $selectedView: computed(() => selectedView()),
      $submissions: computed(() => {
        const _submissions = _submissionEntities();
        if (!_submissions) {
          return [] as SubmissionListItem[];
        }
        return _submissions;
      }),
      $smeList: computed(() => {
        const _sme = _smeEntities();
        if (!_sme) {
          return [];
        }
        return _sme;
      }),
      $qcList: computed(() => {
        const _qc = _qcEntities();
        if (!_qc) {
          return [];
        }
        return _qc;
      }),
      $totalCount: computed(() => total()),
      $isInternal: computed(() => $isInternalSignal()),
      $columns: computed(() => columnsCopy()),
      $isAllSelected: computed(() => isAllSelected()),
      $isIndeterminate: computed(() => isIndeterminate()),
      $selectedRows: computed(() => selectedRows()),
    }),
  ),
  withMethods(
    ({
      _submissionsListService,
      _shallowCamelCasePipe,
      _buildLinkFromValue,
      _wcgTableUtilService,

      ...store
    }) => {
      const _loadSubmissionList = rxMethod<LoadGridPayload>(
        pipe(
          tap(() => patchState(store, { isLoading: true })),
          switchMap(({ params, updateColumns }) =>
            (
              _submissionsListService.loadSubmissionList(
                store.$currentUrl(),
                params,
              ) as Observable<SubmissionListResponse>
            ).pipe(
              map((response) => ({
                ...response,
                config: _shallowCamelCasePipe.transform(response.config),
              })),
              tapResponse({
                error: (error: Error) =>
                  patchState(store, {
                    error: error,
                    isLoading: false,
                    isUpdatingSMEQC: false,
                  }),
                finalize: () =>
                  patchState(store, {
                    isLoading: false,
                    isUpdatingSMEQC: false,
                  }),
                next: (response) => {
                  patchState(store, {
                    currentStateDestroyed: false,
                    total: response.total,
                  });
                  store.loadSubmissions(response.data);
                  const data = response.data;
                  store.loadConfigurations(
                    response.config,
                    updateColumns,
                    data?.[0],
                    _buildLinkFromValue,
                  );
                  store.setGridViews(response.views);
                  store.setAllRows(store.selectedRows(), response.data);
                },
              }),
            ),
          ),
        ),
      );

      const _reloadSubmissionList = (updateColumns = false) => {
        if (store.currentUrl()) {
          const currentParams = _wcgTableUtilService.generateTableQueryParams({
            filters: constructGridFilters(store.$advancedFilters()),
            page: store.$pageView().page || 1,
            pageSize: store.$pageView().pageSize || 25,
            searchValue: store.$searchString(),
            sortParam: {
              dir: store.$sort()?.sortByOrder || 'asc',
              field: store.$sort()?.sortByField || '',
            },
          });
          _loadSubmissionList({
            params: currentParams,
            updateColumns,
          });
        }
      };

      const _getGridMetaData = (
        type: string,
        entityId?: string,
      ): {
        currentUrl: string;
      } => {
        switch (type) {
          case 'home':
            return {
              currentUrl: `${WORKSPACE_URLS.V1}/`,
            };
          case 'study':
            return {
              currentUrl: `${STUDY_URL}${entityId}/workspaces/`,
            };
          case 'site':
            return {
              currentUrl: `${SITE_URL}${entityId}/workspace/`,
            };
          default:
            throw new Error('Invalid type for loading study grid');
        }
      };

      const _setGridMetaData = (
        type: SubmissionGridTypes,
        entityId?: string,
      ) => {
        const { currentUrl } = _getGridMetaData(type, entityId);
        patchState(store, { currentUrl, submissionListScope: type });
      };

      const _loadSMEQCList = (
        attribute: 'sme' | 'qc',
        onSuccess: (data: Record<string, SMEQCUser> | SMEQCUser[]) => void,
      ) => {
        return rxMethod<SMEQCListParams>(
          pipe(
            switchMap((params) =>
              _submissionsListService
                .loadSMEQCList({
                  ...params,
                  attribute,
                })
                .pipe(
                  tapResponse({
                    error: (error: Error) => patchState(store, { error }),
                    next: (response) => {
                      onSuccess(response.data);
                    },
                  }),
                ),
            ),
          ),
        );
      };

      const _loadSMEList = _loadSMEQCList('sme', (data) =>
        store.loadSMEList(data),
      );

      const _loadQCList = _loadSMEQCList('qc', (data) =>
        store.loadQCList(data),
      );

      const _batchUpdateWorkspaceSMEQC = rxMethod<BatchAssignmentsPayload>(
        pipe(
          tap(() =>
            patchState(store, { isLoading: true, isUpdatingSMEQC: true }),
          ),
          switchMap(({ assignments }) =>
            _submissionsListService
              .batchUpdateWorkspaceSMEQC({ assignments })
              .pipe(
                tapResponse({
                  error: (error: Error) =>
                    patchState(store, {
                      error: error,
                      isLoading: false,
                      isUpdatingSMEQC: false,
                    }),
                  next: () => {
                    patchState(store, { isLoading: false });
                    // Reload submissions after successful update
                    _reloadSubmissionList();
                  },
                }),
              ),
          ),
        ),
      );

      const _batchRemoveSMEQC = rxMethod<BatchAssignmentsPayload>(
        pipe(
          tap(() =>
            patchState(store, { isLoading: true, isUpdatingSMEQC: true }),
          ),
          switchMap(({ assignments }) =>
            _submissionsListService.batchRemoveSMEQC({ assignments }).pipe(
              tapResponse({
                error: (error: Error) =>
                  patchState(store, {
                    error: error,
                    isLoading: false,
                    isUpdatingSMEQC: false,
                  }),
                next: () => {
                  patchState(store, { isLoading: false });
                  // Reload submissions after successful removal
                  _reloadSubmissionList();
                },
              }),
            ),
          ),
        ),
      );

      const _restoreSMEQC = (workspace_id: string) => {
        const submission = store
          .$submissions()
          .find((sub) => sub.id === workspace_id);

        if (submission) {
          const updatedSubmission = {
            ...submission,
          };

          store.updateSubmission(workspace_id, updatedSubmission);
        }
      };

      const _updateWorkspaceSMEQC = rxMethod<AssignmentPayload>(
        pipe(
          tap(() =>
            patchState(store, { isLoading: true, isUpdatingSMEQC: true }),
          ),
          switchMap(({ workspace_id, attribute, user_id, user_name }) =>
            _submissionsListService
              .updateWorkspaceSMEQC(workspace_id, {
                attribute,
                user_id,
                user_name,
              })
              .pipe(
                tapResponse({
                  error: (error: Error) => {
                    patchState(store, {
                      error: error,
                      isLoading: false,
                      isUpdatingSMEQC: false,
                    });
                    _restoreSMEQC(workspace_id);
                  },
                  next: () => {
                    patchState(store, {
                      isLoading: false,
                      isUpdatingSMEQC: false,
                    });

                    const submission = store
                      .$submissions()
                      .find((sub) => sub.id === workspace_id);

                    if (submission) {
                      const updatedSubmission = {
                        ...submission,
                        workspace_user_attribute: {
                          sme_user:
                            attribute === 'SME'
                              ? {
                                  id: user_id,
                                  first_name: user_name,
                                }
                              : (submission?.workspace_user_attribute
                                  ?.sme_user ?? null),
                          qc_user:
                            attribute === 'QC'
                              ? {
                                  id: user_id,
                                  first_name: user_name,
                                }
                              : (submission?.workspace_user_attribute
                                  ?.qc_user ?? null),
                        },
                      };

                      store.updateSubmission(workspace_id, updatedSubmission);
                    }
                  },
                }),
              ),
          ),
        ),
      );

      const _removeWorkspaceSMEQC = rxMethod<AssignmentPayload>(
        pipe(
          tap(() =>
            patchState(store, { isLoading: true, isUpdatingSMEQC: true }),
          ),
          switchMap(({ workspace_id, attribute, user_id, user_name }) =>
            _submissionsListService
              .removeWorkspaceSMEQC(workspace_id, {
                attribute,
                user_id,
                user_name,
              })
              .pipe(
                tapResponse({
                  next: () => {
                    patchState(store, {
                      isLoading: false,
                      isUpdatingSMEQC: false,
                    });

                    const submission = store
                      .$submissions()
                      .find((sub) => sub.id === workspace_id);

                    if (submission) {
                      const updatedSubmission = {
                        ...submission,
                        workspace_user_attribute: {
                          sme_user:
                            attribute === 'SME'
                              ? null
                              : (submission?.workspace_user_attribute
                                  ?.sme_user ?? null),
                          qc_user:
                            attribute === 'QC'
                              ? null
                              : (submission?.workspace_user_attribute
                                  ?.qc_user ?? null),
                        },
                      };

                      store.updateSubmission(workspace_id, updatedSubmission);
                    }
                  },
                  error: (error: Error) => {
                    patchState(store, {
                      error: error,
                      isLoading: false,
                      isUpdatingSMEQC: false,
                    });
                    _restoreSMEQC(workspace_id);
                  },
                }),
              ),
          ),
        ),
      );
      return {
        resetGridSettings: (payload: LoadGridPayload) => {
          _loadSubmissionList(payload);
          patchState(store, { searchString: '', ...DEFAULT_CHECKBOX_PROPS });
          store.setDefaultGridView();
        },
        resetState: () =>
          patchState(store, {
            ...submissionListInitialState,
            currentStateDestroyed: true,
          }),
        setByURL: (type: SubmissionGridTypes, entityId?: string) =>
          _setGridMetaData(type, entityId),
        loadSubmissionsGrid: _loadSubmissionList,
        loadSMEList: _loadSMEList,
        loadQCList: _loadQCList,
        selectAll: (checked: boolean) =>
          store.selectAll(store.selectedRows(), store.allRows(), checked),
        selectRow: (row: SubmissionListItem, checked: boolean) =>
          store.selectRow(store.selectedRows(), store.allRows(), row, checked),
        resetSelectedRows: () =>
          patchState(store, {
            selectedRows: [],
            isAllSelected: false,
            isIndeterminate: false,
          }),
        batchUpdateWorkspaceSMEQC: _batchUpdateWorkspaceSMEQC,
        batchRemoveSMEQC: _batchRemoveSMEQC,
        updateWorkspaceSMEQC: _updateWorkspaceSMEQC,
        removeWorkspaceSMEQC: _removeWorkspaceSMEQC,
        reloadSubmissionList: _reloadSubmissionList,
      };
    },
  ),
) satisfies Type<SubmissionListSignalState>;
